import { MapLocation } from '@/types/location';

type MinimalLocation = Pick<MapLocation, 'id' | 'name' | 'devices' | 'mediaOwner' | 'region'>;

export class LocationSelectionManager {
  static updateSelections(
    newSelected: Set<number>,
    onDeviceSelect: (devices: number) => void,
    onLocationsChange: (locations: Array<{ name: string; devices: number }>) => void,
    locations: MapLocation[],
    attentionScreenCounts?: Map<number, number>
  ) {
    const totalDevices = Array.from(newSelected).reduce((sum, id) => {
      const loc = locations.find(l => l.id === id);
      if (!loc) return sum;
      // Use custom screen count for Attention locations if available
      if (loc.mediaOwner === 'Attention' && attentionScreenCounts?.has(id)) {
        return sum + (attentionScreenCounts.get(id) || loc.devices);
      }
      return sum + (loc.devices || 0);
    }, 0);

    onDeviceSelect(totalDevices);

    const selectedLocationsList = Array.from(newSelected)
      .map(id => {
        const loc = locations.find(l => l.id === id);
        if (!loc) return null;
        // Use custom screen count for Attention locations if available
        let deviceCount = loc.devices;
        if (loc.mediaOwner === 'Attention' && attentionScreenCounts?.has(id)) {
          deviceCount = attentionScreenCounts.get(id) || loc.devices;
        }
        return { id: loc.id, name: loc.name, devices: deviceCount, mediaOwner: loc.mediaOwner, region: loc.region };
      })
      .filter((loc) => loc !== null) as any;

    onLocationsChange(selectedLocationsList);
    return newSelected;
  }

  static getLocationTypes(region: string, locations: MapLocation[]): string[] {
    const filteredLocations = locations.filter(loc => 
      region === 'all' ? true : loc.region === region
    );
    return [...new Set(filteredLocations.map(loc => loc.type))];
  }

  static toggleAllInRegion(
    region: string, 
    selectedLocations: Set<number>, 
    locations: MapLocation[],
    activeMediaOwner: string = 'all'
  ): Set<number> {
    // Filter locations to only include those in the specified region and matching the media owner
    const regionLocations = locations.filter(location => {
      const regionMatch = location.region === region;
      const mediaOwnerMatch = activeMediaOwner === 'all' ? true : location.mediaOwner === activeMediaOwner;
      const notInMaintenance = location.maintenanceStatus !== 'maintenance' && location.maintenanceStatus !== 'offline';
      return regionMatch && mediaOwnerMatch && notInMaintenance;
    });
    
    console.log(`Toggling all in ${region} for media owner ${activeMediaOwner}. Found ${regionLocations.length} locations.`);
    
    // Create a set of location IDs for the region
    const regionLocationIds = new Set(regionLocations.map(loc => loc.id));
    
    // Check if ALL locations in this region are already selected
    // by checking if every location ID from the region is in the selectedLocations set
    const allSelected = Array.from(regionLocationIds).every(id => selectedLocations.has(id));
    
    console.log(`All locations in ${region} selected? ${allSelected}`);
    
    // Create a new Set to avoid mutating the original
    const newSelected = new Set(selectedLocations);
    
    if (allSelected) {
      // If all are selected, remove all locations in this region
      regionLocations.forEach(location => {
        newSelected.delete(location.id);
      });
      console.log(`Deselected all ${regionLocations.length} locations in ${region}`);
    } else {
      // If not all are selected, add all locations in this region
      regionLocations.forEach(location => {
        newSelected.add(location.id);
      });
      console.log(`Selected all ${regionLocations.length} locations in ${region}`);
    }
    
    return newSelected;
  }

  static toggleTypeInRegion(
    region: string, 
    type: string, 
    selectedLocations: Set<number>,
    setSelectedTypes: (cb: (prev: Set<string>) => Set<string>) => void,
    locations: MapLocation[],
    activeMediaOwner: string = 'all'
  ): Set<number> {
    const locationsOfType = locations.filter(location => {
      const regionMatch = location.region === region;
      const typeMatch = location.type === type;
      const mediaOwnerMatch = activeMediaOwner === 'all' ? true : location.mediaOwner === activeMediaOwner;
      const notInMaintenance = location.maintenanceStatus !== 'maintenance' && location.maintenanceStatus !== 'offline';
      return regionMatch && typeMatch && mediaOwnerMatch && notInMaintenance;
    });
    
    console.log(`Toggling ${type} in ${region} for media owner ${activeMediaOwner}. Found ${locationsOfType.length} locations.`);
    
    const locationIds = new Set(locationsOfType.map(loc => loc.id));
    const allSelected = locationsOfType.every(loc => selectedLocations.has(loc.id));
    
    const newSelected = new Set(selectedLocations);
    if (allSelected) {
      locationIds.forEach(id => newSelected.delete(id));
      setSelectedTypes(prev => {
        const next = new Set(prev);
        next.delete(type);
        return next;
      });
    } else {
      locationIds.forEach(id => newSelected.add(id));
      setSelectedTypes(prev => {
        const next = new Set(prev);
        next.add(type);
        return next;
      });
    }
    
    return newSelected;
  }
}
